import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import {
  directoryApi,
  type CreateOrganisationNominalRequest,
  type OrganisationNominal,
  type UpdateOrganisationNominalRequest,
} from '../client';

/** Stable query-key factory for nominal-code-scoped caches. */
export const organisationNominalKeys = {
  all: ['directory', 'organisation-nominal'] as const,
  byOrganisation: (organisationId: string) =>
    ['directory', 'organisation-nominal', 'org', organisationId] as const,
};

/** Fetch every nominal code for an organisation. Pass `null` to keep disabled. */
export function useOrganisationNominals(
  organisationId: string | null,
  options?: Omit<UseQueryOptions<OrganisationNominal[], Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<OrganisationNominal[], Error>({
    queryKey: organisationId
      ? organisationNominalKeys.byOrganisation(organisationId)
      : organisationNominalKeys.all,
    queryFn: () => {
      if (!organisationId) throw new Error('No organisation id');
      return directoryApi.getOrganisationNominals(organisationId);
    },
    enabled: !!organisationId,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

/** Mutation wrapper around POST `/organisationNominals`. */
export function useCreateOrganisationNominal() {
  const qc = useQueryClient();
  return useMutation<string, Error, CreateOrganisationNominalRequest>({
    mutationKey: [...organisationNominalKeys.all, 'create'],
    mutationFn: (body) => directoryApi.createOrganisationNominal(body),
    onSuccess: (_id, vars) => {
      qc.invalidateQueries({
        queryKey: organisationNominalKeys.byOrganisation(vars.organisationId),
      });
    },
  });
}

/**
 * Mutation wrapper around PUT `/organisationNominals`. The caller passes
 * `organisationId` alongside the body so the per-org list-cache key is
 * invalidated correctly (the wire body itself only carries `nominalId`).
 */
export function useUpdateOrganisationNominal() {
  const qc = useQueryClient();
  return useMutation<
    string,
    Error,
    { organisationId: string; body: UpdateOrganisationNominalRequest }
  >({
    mutationKey: [...organisationNominalKeys.all, 'update'],
    mutationFn: ({ body }) => directoryApi.updateOrganisationNominal(body),
    onSuccess: (_id, { organisationId }) => {
      qc.invalidateQueries({
        queryKey: organisationNominalKeys.byOrganisation(organisationId),
      });
    },
  });
}

/** Mutation wrapper around DELETE `/organisationNominals/{id}`. */
export function useDeleteOrganisationNominal() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { organisationId: string; id: string }>({
    mutationKey: [...organisationNominalKeys.all, 'delete'],
    mutationFn: ({ id }) => directoryApi.deleteOrganisationNominal(id),
    onSuccess: (_res, { organisationId }) => {
      qc.invalidateQueries({
        queryKey: organisationNominalKeys.byOrganisation(organisationId),
      });
    },
  });
}
